import { useState, useEffect } from "react";
import { Quote, Star, ChevronRight, ChevronLeft } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const AVATAR_COLORS = ["bg-[#0891b2]", "bg-[#ef4444]", "bg-amber-600", "bg-emerald-600", "bg-purple-600"];

export function MaintenanceReviews() {
  const { t, isRTL } = useLang();
  const reviews = t.maintenanceReviews.items;
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setIdx((i) => (i + 1) % reviews.length), 6000);
    return () => clearInterval(timer);
  }, [reviews.length]);

  const NextIcon = isRTL ? ChevronLeft : ChevronRight;
  const PrevIcon = isRTL ? ChevronRight : ChevronLeft;

  return (
    <section className="py-20 bg-white" data-testid="maintenance-reviews">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-block text-[10px] tracking-[0.3em] text-[#ef4444] font-semibold mb-3 uppercase">
            {t.maintenanceReviews.eyebrow}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0891b2] mb-3 leading-tight">
            {t.maintenanceReviews.title}
          </h2>
          <p className="text-slate-600">{t.maintenanceReviews.subtitle}</p>
        </div>

        {/* Main review card */}
        <div className="relative bg-gradient-to-br from-cyan-50 via-white to-red-50/30 rounded-3xl border border-slate-100 p-8 sm:p-12 shadow-sm overflow-hidden">
          <Quote className="absolute top-8 end-8 w-24 h-24 text-[#0891b2]/10" />
          <div className="relative min-h-[220px]">
            {reviews.map((r, i) => (
              <div
                key={i}
                data-testid={`mrev-${i}`}
                className={`transition-opacity duration-700 ${
                  i === idx ? "opacity-100 relative" : "opacity-0 absolute inset-0 pointer-events-none"
                }`}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star key={s} className="w-4 h-4 fill-[#ef4444] text-[#ef4444]" />
                  ))}
                </div>
                <p className="text-lg sm:text-xl text-slate-700 leading-relaxed font-display mb-6 max-w-3xl">
                  &ldquo;{r.quote}&rdquo;
                </p>
                <div className="flex items-center gap-4 pt-4 border-t border-slate-200/60">
                  <div className={`w-12 h-12 rounded-full ${AVATAR_COLORS[i % AVATAR_COLORS.length]} text-white flex items-center justify-center font-display shrink-0`}>
                    {r.initials}
                  </div>
                  <div>
                    <div className="font-display text-base text-[#0891b2]">{r.name}</div>
                    <div className="text-xs text-slate-500">{r.role}</div>
                  </div>
                  <div className="ms-auto text-end">
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{r.metricLabel}</div>
                    <div className="font-display text-xl text-[#ef4444]">{r.metricValue}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dots + arrows */}
        <div className="flex items-center justify-center gap-4 mt-6">
          <button
            type="button"
            onClick={() => setIdx((i) => (i - 1 + reviews.length) % reviews.length)}
            data-testid="mrev-prev"
            className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:border-[#0891b2] hover:text-[#0891b2] transition-colors"
          >
            <PrevIcon className="w-4 h-4" />
          </button>
          <div className="flex gap-1.5">
            {reviews.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIdx(i)}
                data-testid={`mrev-dot-${i}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-8 bg-[#ef4444]" : "w-1.5 bg-slate-300"
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => setIdx((i) => (i + 1) % reviews.length)}
            data-testid="mrev-next"
            className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:border-[#0891b2] hover:text-[#0891b2] transition-colors"
          >
            <NextIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
